import { observer } from 'mobx-react-lite'
import { useEffect, useState } from 'react'
import { toApiError } from '@/api/errors'
import { Button } from '@/components/ui/Button'
import { Dialog } from '@/components/ui/Dialog'
import { Field } from '@/components/ui/Field'
import { AVATAR_CROP } from '@/components/ui/ImageCropDialog'
import { ImageUpload, isUploading } from '@/components/ui/ImageUpload'
import { InlineAlert } from '@/components/ui/InlineAlert'
import { Input } from '@/components/ui/Input'
import { PasswordInput } from '@/features/auth/PasswordInput'
import { useT } from '@/i18n'
import { pickName } from '@/lib/format'
import { loginProblem } from '@/lib/login'
import { passwordProblem } from '@/lib/password'
import { meService } from '@/services'
import { useStores } from '@/stores'
import type { UserIdentity } from '@/types/api'

/**
 * «Мой профиль» from the account menu: every signed-in user edits their own
 * photo, ФИО and login and changes their password (GET/PATCH /me/account,
 * POST /me/password; no Users right). The phone, role and branches stay with
 * the administrator. A changed login or password ends every session, so the
 * user signs in again. Remount it (key) on every opening.
 */
export const MyAccountDialog = observer(function MyAccountDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const t = useT()
  const { session, auth, toasts, ui } = useStores()
  const [account, setAccount] = useState<UserIdentity | null>(null)
  const [displayName, setDisplayName] = useState('')
  const [login, setLogin] = useState('')
  const [avatarUrl, setAvatarUrl] = useState('')
  const [currentPassword, setCurrentPassword] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [repeatPassword, setRepeatPassword] = useState('')
  const [submitted, setSubmitted] = useState(false)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!open) return
    let alive = true
    meService
      .account()
      .then((loaded) => {
        if (!alive) return
        setAccount(loaded)
        setDisplayName(loaded.display_name)
        setLogin(loaded.login)
        setAvatarUrl(loaded.avatar_url ?? '')
      })
      .catch((cause) => alive && setError(toApiError(cause).message || t('layout.myAccount.failed')))
    return () => {
      alive = false
    }
  }, [open, t])

  const changingPassword = Boolean(currentPassword || newPassword || repeatPassword)
  const problems = {
    displayName: displayName.trim() ? undefined : t('validation.required'),
    login: login.trim() ? loginProblem(login, t, account?.login) : t('validation.required'),
    currentPassword: changingPassword && !currentPassword ? t('validation.required') : undefined,
    newPassword: changingPassword ? (passwordProblem(newPassword, t) ?? undefined) : undefined,
    repeatPassword: changingPassword && repeatPassword !== newPassword ? t('validation.passwordMismatch') : undefined,
  }
  const profileChanges: { display_name?: string; login?: string; avatar_url?: string } = {}
  if (account) {
    if (displayName.trim() !== account.display_name) profileChanges.display_name = displayName.trim()
    if (login.trim() !== account.login) profileChanges.login = login.trim()
    if (avatarUrl !== (account.avatar_url ?? '')) profileChanges.avatar_url = avatarUrl
  }
  const dirty = Object.keys(profileChanges).length > 0 || changingPassword

  const save = async () => {
    setSubmitted(true)
    if (!account || saving || !dirty || isUploading(avatarUrl) || Object.values(problems).some(Boolean)) return
    setSaving(true)
    setError(null)
    try {
      if (Object.keys(profileChanges).length > 0) {
        await meService.updateAccount(profileChanges)
        if ('avatar_url' in profileChanges) session.setAvatarUrl(avatarUrl)
      }
      if (changingPassword) await meService.changePassword(currentPassword, newPassword)
      // Both end every session of the user: sign in again with the new credentials.
      if ('login' in profileChanges || changingPassword) {
        toasts.success(t('layout.myAccount.signInAgain'))
        onClose()
        await auth.logout()
        return
      }
      toasts.success(t('layout.myAccount.saved'))
      if ('display_name' in profileChanges) await session.reload()
      onClose()
    } catch (cause) {
      setError(toApiError(cause).message || t('layout.myAccount.failed'))
    } finally {
      setSaving(false)
    }
  }

  const shown = (problem: string | undefined) => (submitted ? problem : undefined)

  return (
    <Dialog
      open={open}
      onClose={onClose}
      size="md"
      title={t('layout.myAccount.title')}
      description={t('layout.myAccount.description')}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            {t('common.actions.cancel')}
          </Button>
          <Button variant="primary" loading={saving} disabled={!account || !dirty || isUploading(avatarUrl)} onClick={() => void save()}>
            {t('common.actions.save')}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <Field label={t('layout.myAccount.photo')}>
          <ImageUpload value={avatarUrl} onChange={setAvatarUrl} disabled={saving || !account} reportPending crop={AVATAR_CROP} />
        </Field>
        <Field label={t('usersByRole.fullName')} required error={shown(problems.displayName)}>
          <Input value={displayName} onChange={(event) => setDisplayName(event.target.value)} disabled={!account} autoComplete="name" />
        </Field>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Field label={t('usersByRole.login')} required error={shown(problems.login)}>
            <Input className="num" value={login} onChange={(event) => setLogin(event.target.value)} disabled={!account} autoComplete="username" />
          </Field>
          <Field label={t('common.fields.phone')} hint={t('layout.myAccount.phoneHint')}>
            <Input className="num" value={account?.phone ?? ''} readOnly aria-readonly tabIndex={-1} />
          </Field>
        </div>
        {account?.role && (
          <Field label={t('layout.myAccount.role')}>
            <Input value={pickName(account.role, ui.contentLocale)} readOnly aria-readonly tabIndex={-1} />
          </Field>
        )}
        <div className="flex flex-col gap-3 border-t border-line pt-4">
          <div className="text-[length:calc(13px*var(--text-scale))] font-medium text-fg">{t('layout.myAccount.password')}</div>
          <Field label={t('layout.myAccount.currentPassword')} error={shown(problems.currentPassword)}>
            <PasswordInput value={currentPassword} autoComplete="current-password" onChange={(event) => setCurrentPassword(event.target.value)} />
          </Field>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Field label={t('layout.myAccount.newPassword')} hint={t('validation.passwordRule')} error={shown(problems.newPassword)}>
              <PasswordInput value={newPassword} autoComplete="new-password" onChange={(event) => setNewPassword(event.target.value)} />
            </Field>
            <Field label={t('layout.myAccount.repeatPassword')} error={shown(problems.repeatPassword)}>
              <PasswordInput value={repeatPassword} autoComplete="new-password" onChange={(event) => setRepeatPassword(event.target.value)} />
            </Field>
          </div>
        </div>
        {error && <InlineAlert tone="danger">{error}</InlineAlert>}
      </div>
    </Dialog>
  )
})
